import { GetScopeSearchRequest, GetScopeValuesRequest, ScopeItem, TopologyService } from "../../Scopes.types";
import { getSelectedEnvironment, useSubstrateEnvironmentRingTopologyService } from "./useSubstrateEnvironmentRingTopologyService";

import { CentralAdminEntityType } from "Enums/CentralAdminEntityType";
import { Constants } from "Shared/Constants";
import { centralAdminLocationSchema } from "Shared/Components/Scope/Topology/CentralAdminTopology";
import { useScopeSearch } from "../useScopeSearch";
import { useSubstrateRegion } from "./useSubstrateRegion";
import { z } from "zod";
import { useORPCClient } from "Core/oRPC";

export const useSubstratePhysicalTopologyService = (): TopologyService => {
    const { inventoryApi } = useORPCClient();
    const envRingTopologyService = useSubstrateEnvironmentRingTopologyService();
    const { getRegionList } = useSubstrateRegion();
    const { getScopeSearchResult } = useScopeSearch();

    return {
        getScopeValueOptions: async (request: GetScopeValuesRequest): Promise<ScopeItem[]> => {
            const currentScopeTarget = request.currentScopeTarget;
            const environment = getSelectedEnvironment(request.selectedScopesSoFar || []);
            const workload = request.context?.Workload ?? Constants.defaultWorkload;

            switch (currentScopeTarget.ScopeType.toLowerCase()) {
                case "region":
                    return getRegionList(environment, workload);
                case "location":
                    const selectedRegion = request.selectedScopesSoFar?.find((scope) => scope.ScopeType?.toLowerCase() === "region");
                    const locationRequest = {
                        Environment: environment,
                        Workload: workload || "",
                        TargetType: CentralAdminEntityType.Location,
                        FilterType: CentralAdminEntityType.Region,
                        FilterValue: selectedRegion?.Value?.toUpperCase()
                    };
                    const locationOut = await inventoryApi.postTopology(locationRequest);
                    const locationParsedOutput = centralAdminLocationSchema.array().parse(locationOut);
                    return locationParsedOutput.map((item) => ({
                        ScopeType: "Location",
                        Value: item.Name
                    }));
                case "rack":
                    const selectedLocation = request.selectedScopesSoFar?.find((scope) => scope.ScopeType?.toLowerCase() === "location");
                    const rackRequest = {
                        Environment: environment,
                        Workload: workload || "",
                        TargetType: CentralAdminEntityType.Rack,
                        FilterType: CentralAdminEntityType.Location,
                        FilterValue: selectedLocation?.Value?.toUpperCase()
                    };
                    const rackOut = await inventoryApi.postTopology(rackRequest);
                    const rackParsedOutput = z.array(z.string()).parse(rackOut);
                    return rackParsedOutput.map((val) => ({
                        ScopeType: "Rack",
                        Value: val
                    }));
                case "loadbalancer":
                    const selectedLoadBalancerLocation = request.selectedScopesSoFar?.find((scope) => scope.ScopeType?.toLowerCase() === "location");
                    const lbRequest = {
                        Environment: environment,
                        Workload: workload || "",
                        TargetType: CentralAdminEntityType.LoadBalancer,
                        FilterType: CentralAdminEntityType.Location,
                        FilterValue: selectedLoadBalancerLocation?.Value?.toUpperCase()
                    };
                    const lbOut = await inventoryApi.postTopology(lbRequest);
                    const lbParsedOutput = z.array(z.string()).parse(lbOut);
                    return lbParsedOutput.map((val) => ({
                        ScopeType: "LoadBalancer",
                        Value: val
                    }));
                default:
                    return envRingTopologyService.getScopeValueOptions(request);
            }
        },
        getScopeSearch: (request: GetScopeSearchRequest): Promise<ScopeItem[]> => {
            const validTargetTypes = [
                CentralAdminEntityType.Region,
                CentralAdminEntityType.Location,
                CentralAdminEntityType.Rack,
                CentralAdminEntityType.LoadBalancer
            ];
            return getScopeSearchResult(request, validTargetTypes);
        }
    };
};
